import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/Badge'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Container, ContainerHeader, ContainerTitle, ContainerContent } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { toast } from '@/hooks/use-toast'
import { useOrganizationDetail } from '@/features/admin/organizationsQueries'
import { useOrganizationWorkflows, useUpdateWorkflow } from '@/features/admin/workflowsQueries'
import { useWorkflowEmailSettings } from '@/features/admin/workflowSetupQueries'
import { WorkflowConnectionCards } from '@/features/admin/components/WorkflowConnectionCards'
import { WorkflowInfo } from '@/features/admin/components/WorkflowInfo'
import { WORKFLOW_TYPES, type WorkflowStartMode, type WorkflowStatus } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

const STATUS_OPTIONS: { value: WorkflowStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
]

const START_MODE_OPTIONS: { value: WorkflowStartMode; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'scheduled', label: 'Scheduled' },
]

export function WorkflowSetupPage() {
  const { organizationId, workflowId } = useParams<{ organizationId: string; workflowId: string }>()

  const { data: org } = useOrganizationDetail(organizationId)
  const { data: workflows } = useOrganizationWorkflows(organizationId, 'all')
  const workflow = workflows?.find((wf) => wf.id === workflowId)
  const typeLabel = WORKFLOW_TYPES.find((t) => t.value === workflow?.type)?.label ?? workflow?.type
  const updateWorkflow = useUpdateWorkflow(organizationId ?? '')
  const { data: email } = useWorkflowEmailSettings(organizationId, workflowId)
  const mailboxConfigured = !!email?.address

  const [status, setStatus] = useState<WorkflowStatus | undefined>(workflow?.status)
  const [startMode, setStartMode] = useState<WorkflowStartMode | undefined>(workflow?.start_mode)

  useEffect(() => {
    setStatus(workflow?.status)
    setStartMode(workflow?.start_mode)
  }, [workflow?.status, workflow?.start_mode])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!workflow || !status || !startMode) return
    try {
      await updateWorkflow.mutateAsync({
        workflowId: workflow.id,
        name: workflow.name,
        status,
        start_mode: startMode,
      })
      toast({ title: 'Workflow updated' })
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not update workflow',
      })
    }
  }

  return (
    <div className="container mx-auto py-4">
      <Link
        to="/admin/workflows"
        className="mb-4 inline-flex items-center gap-1 text-sm text-on-canvas-muted hover:text-on-canvas"
      >
        <ChevronLeft className="h-4 w-4" />
        Workflows
      </Link>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight text-on-canvas">
            {workflow?.name ?? 'Workflow'}
          </h1>
          {workflow && (
            <Badge tone={workflow.audience === 'customer' ? 'success' : 'warning'} size="md">
              {workflow.audience === 'customer' ? 'Customer' : 'Test'}
            </Badge>
          )}
        </div>
        <span className="text-xs font-semibold text-on-canvas-muted">
          {org?.name}
          {typeLabel ? ` · ${typeLabel}` : ''}
        </span>
      </div>

      {organizationId && workflowId && (
        <WorkflowConnectionCards
          organizationId={organizationId}
          workflowId={workflowId}
          workflowType={workflow?.type}
        />
      )}

      {workflow && status && startMode && (
        <Container className="mt-6">
          <ContainerHeader>
            <ContainerTitle>Status &amp; trigger</ContainerTitle>
          </ContainerHeader>
          <ContainerContent>
            <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </label>
                <Select value={status} onValueChange={(v) => setStatus(v as WorkflowStatus)}>
                  <SelectTrigger className="h-9 w-[140px]">
                    {/* Radix only knows an item's label once its (portal-rendered,
                        closed-by-default) content has mounted at least once —
                        without this, the trigger shows blank until first opened. */}
                    <SelectValue>{STATUS_OPTIONS.find((s) => s.value === status)?.label}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Trigger
                </label>
                <div className="flex items-center gap-2">
                  <Select
                    value={startMode}
                    onValueChange={(v) => setStartMode(v as WorkflowStartMode)}
                    disabled={workflow.audience === 'test'}
                  >
                    <SelectTrigger className="h-9 w-[140px]">
                      <SelectValue>
                        {START_MODE_OPTIONS.find((s) => s.value === startMode)?.label}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {START_MODE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          disabled={s.value === 'scheduled' && !mailboxConfigured}
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {workflow.audience === 'test' ? (
                    <p className="text-xs text-gray-400">Test workflows can only run manually.</p>
                  ) : (
                    !mailboxConfigured && (
                      <p className="text-xs text-gray-400">
                        Configure the mailbox before this can be scheduled.
                      </p>
                    )
                  )}
                </div>
              </div>

              <Button type="submit" disabled={updateWorkflow.isPending}>
                {updateWorkflow.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </form>
          </ContainerContent>
        </Container>
      )}

      {organizationId && workflowId && (
        <WorkflowInfo organizationId={organizationId} workflowId={workflowId} />
      )}
    </div>
  )
}
